/**
 * Free Product Cart Feature
 * Automatically adds/removes a free product based on cart threshold
 */
(function() {
  if (!window.theme?.freeProduct?.enabled) return;

  const { variantId, exclusions, threshold } = window.theme.freeProduct;
  if (!variantId || threshold <= 0) return;

  let isProcessing = false;
  let pendingCheck = false;
  let debounceTimeout = null;

  // Calculate cart total excluding excluded products
  function getEligibleCartTotal(cart) {
    return cart.items.reduce((total, item) => {
      // Skip excluded products
      if (exclusions.includes(item.product_id)) return total;
      // Skip the free product itself
      if (item.variant_id === variantId) return total;
      return total + item.final_line_price;
    }, 0);
  }

  // Check if free product is in cart
  function hasFreeProduct(cart) {
    return cart.items.some(item => item.variant_id === variantId);
  }

  // Add free product to cart
  async function addFreeProduct() {
    try {
      await fetch(window.theme.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      return true;
    } catch (e) {
      console.error('Failed to add free product:', e);
      return false;
    }
  }

  // Remove free product from cart
  async function removeFreeProduct(cart) {
    const item = cart.items.find(i => i.variant_id === variantId);
    if (!item) return false;

    try {
      await fetch(window.theme.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: 0 })
      });
      return true;
    } catch (e) {
      console.error('Failed to remove free product:', e);
      return false;
    }
  }

  // Refresh cart UI and update counter
  async function refreshCart() {
    // Fetch updated cart to get accurate count
    const response = await fetch(window.theme.routes.cart_url + '.js');
    const updatedCart = await response.json();

    // Update cart counter
    document.dispatchEvent(new CustomEvent('theme:cart:change', {
      bubbles: true,
      detail: { cartCount: updatedCart.item_count }
    }));

    // Refresh cart drawer contents
    document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
  }

  // Main check function (debounced)
  function checkFreeProduct() {
    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(doCheck, 100);
  }

  async function doCheck() {
    if (isProcessing) {
      pendingCheck = true;
      return;
    }
    isProcessing = true;

    try {
      const response = await fetch(window.theme.routes.cart_url + '.js');
      const cart = await response.json();

      const eligibleTotal = getEligibleCartTotal(cart);
      const hasProduct = hasFreeProduct(cart);

      if (eligibleTotal >= threshold && !hasProduct) {
        // Add free product
        if (await addFreeProduct()) {
          await refreshCart();
        }
      } else if (eligibleTotal < threshold && hasProduct) {
        // Remove free product
        if (await removeFreeProduct(cart)) {
          await refreshCart();
        }
      }
    } catch (e) {
      console.error('Free product check failed:', e);
    } finally {
      isProcessing = false;
      // Process any pending check that came in while we were busy
      if (pendingCheck) {
        pendingCheck = false;
        doCheck();
      }
    }
  }

  // Listen for cart updates
  document.addEventListener('theme:cart:change', checkFreeProduct);
  document.addEventListener('theme:cart:add', checkFreeProduct);

  // Check on page load
  document.addEventListener('DOMContentLoaded', checkFreeProduct);
})();
